import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
  Briefcase,
  AlertTriangle,
  Clock,
  CheckCircle2,
  DollarSign,
  Activity,
  TrendingUp,
  PieChart,
  ArrowUpRight,
  Zap,
  Layers,
  SlidersHorizontal,
  X,
  Sparkles,
  Copy,
  Check,
  Mail,
  Printer,
} from 'lucide-react';
import { api } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { OnboardingChecklist } from '@/components/layout/OnboardingChecklist.js';
import { PullToRefresh } from '@/components/ui/PullToRefresh.js';
import { Skeleton, SkeletonStat, SkeletonCard } from '@/components/ui/skeleton.js';
import { PlatformBadge } from '@/components/ui/PlatformBadge.js';
import { DeliveryCountdownBadge } from '@/components/ui/DeliveryCountdownBadge.js';
import { PlatformProfitabilityCard } from '@/components/platform/PlatformProfitabilityCard.js';
import { calculatePlatformCut, calculateEffectiveHourlyYield } from '@/lib/platformFees.js';

interface DashboardCustomization {
  activeProjects: boolean;
  overdue: boolean;
  inReview: boolean;
  finances: boolean;
  teamActivity: boolean;
  weeklyTrend: boolean;
  standupDigest?: boolean;
  cardOrder?: string[];
}

const DEFAULT_CARD_ORDER = ['activeProjects', 'inReview', 'overdue', 'timeToday'];

const DEFAULT_CUSTOMIZATION: DashboardCustomization = {
  activeProjects: true,
  overdue: true,
  inReview: true,
  finances: true,
  teamActivity: true,
  weeklyTrend: true,
  standupDigest: true,
  cardOrder: DEFAULT_CARD_ORDER,
};

interface DayTrend {
  day: string;
  date: string;
  hours: number;
  billable_percent?: number;
  tasks_count?: number;
}

interface ProjectProgress {
  id: string;
  name: string;
  client_name?: string;
  category?: string;
  priority?: string;
  progress_percent: number;
  progress_mode?: 'automatic' | 'manual';
  manual_override_reason?: string | null;
  total_tasks?: number;
  completed_tasks?: number;
  recorded_hours?: number;
  deadline?: string;
  status: string;
  platform?: string | null;
  platform_order_id?: string | null;
  platform_url?: string | null;
  budget?: number | null;
  portal_token?: string | null;
  fiverr_revisions_remaining?: number | null;
  fiverr_order_status?: string | null;
  upwork_escrow_status?: string | null;
  upwork_weekly_limit_hours?: number | null;
  direct_invoice_status?: string | null;
}

export function DashboardPage() {
  const { user } = useAuth();
  const canCustomize = user?.role === 'owner' || user?.role === 'manager';

  const [customization, setCustomization] = useState<DashboardCustomization>(() => {
    try {
      const saved = localStorage.getItem('tsos_dashboard_widgets');
      return saved ? { ...DEFAULT_CUSTOMIZATION, ...JSON.parse(saved) } : DEFAULT_CUSTOMIZATION;
    } catch {
      return DEFAULT_CUSTOMIZATION;
    }
  });
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);

  const saveCustomization = (next: DashboardCustomization) => {
    setCustomization(next);
    try {
      localStorage.setItem('tsos_dashboard_widgets', JSON.stringify(next));
    } catch {}
  };

  const moveCardOrder = (index: number, direction: 'up' | 'down') => {
    const currentOrder = [...(customization.cardOrder || DEFAULT_CARD_ORDER)];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= currentOrder.length) return;
    const temp = currentOrder[index];
    currentOrder[index] = currentOrder[targetIndex];
    currentOrder[targetIndex] = temp;
    saveCustomization({
      ...customization,
      cardOrder: currentOrder,
    });
  };

  const [data, setData] = useState<any>(null);
  const [projectsList, setProjectsList] = useState<ProjectProgress[]>([]);
  const [tasksList, setTasksList] = useState<any[]>([]);
  const [digest, setDigest] = useState<any | null>(null);
  const [copiedDigest, setCopiedDigest] = useState(false);
  const [executiveBrief, setExecutiveBrief] = useState<any | null>(null);
  const [copiedBrief, setCopiedBrief] = useState(false);
  const [emailingBrief, setEmailingBrief] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [channelStream, setChannelStream] = useState<'all' | 'fiverr' | 'upwork' | 'direct'>('all');

  // Chart interaction states
  const [hoveredPointIndex, setHoveredPointIndex] = useState<number | null>(null);
  const [hoveredStatus, setHoveredStatus] = useState<string | null>(null);

  const handleCopyDigest = async () => {
    const briefText = digest?.formatted_brief || digest?.executive_brief_markdown;
    if (!briefText) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(briefText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = briefText;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedDigest(true);
      setTimeout(() => setCopiedDigest(false), 2500);
    } catch (err) {
      console.error('Failed to copy digest:', err);
    }
  };

  const handleCopyBrief = async () => {
    const briefText = executiveBrief?.brief_markdown || '';
    if (!briefText) return;
    try {
      await navigator.clipboard.writeText(briefText);
      setCopiedBrief(true);
      setTimeout(() => setCopiedBrief(false), 2500);
    } catch (err) {
      console.error('Failed to copy brief:', err);
    }
  };

  const handleEmailBrief = async () => {
    setEmailingBrief(true);
    try {
      await api.post('/api/v1/reports/executive-brief/email', {});
      setEmailSuccess(true);
      setTimeout(() => setEmailSuccess(false), 3500);
    } catch (err) {
      console.error('Failed to email brief:', err);
    } finally {
      setEmailingBrief(false);
    }
  };

  const handlePrintBrief = () => {
    window.print();
  };

  const loadDashboardData = async () => {
    try {
      const [summaryRes, projectsRes, tasksRes, digestRes, briefRes] = await Promise.allSettled([
        api.get<{ data: any }>('/api/v1/dashboard/summary'),
        api.get<{ data: any }>('/api/v1/projects'),
        api.get<{ data: any }>('/api/v1/tasks'),
        api.get<{ data: any }>('/api/v1/standups/digest'),
        api.get<{ data: any }>('/api/v1/reports/executive-brief'),
      ]);

      if (summaryRes.status === 'fulfilled' && summaryRes.value?.data) {
        setData(summaryRes.value.data);
      }
      if (projectsRes.status === 'fulfilled' && projectsRes.value?.data) {
        const raw = Array.isArray(projectsRes.value.data) ? projectsRes.value.data : [];
        setProjectsList(raw);
      }
      if (tasksRes.status === 'fulfilled' && tasksRes.value?.data) {
        const raw = Array.isArray(tasksRes.value.data) ? tasksRes.value.data : [];
        setTasksList(raw);
      }
      if (digestRes.status === 'fulfilled' && digestRes.value?.data) {
        const d = digestRes.value.data;
        if (d && (d.date || d.summary)) {
          setDigest(d);
        }
      }
      if (briefRes.status === 'fulfilled' && briefRes.value?.data) {
        setExecutiveBrief(briefRes.value.data);
      }
    } catch (err) {
      console.error('Failed to load dashboard data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Header Skeleton */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-72" />
          </div>
          <div className="flex items-center space-x-2">
            <Skeleton className="h-9 w-28 rounded-lg" />
          </div>
        </div>

        {/* KPI Stats Skeleton Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SkeletonStat />
          <SkeletonStat />
          <SkeletonStat />
          <SkeletonStat />
        </div>

        {/* Analytics Charts Skeleton Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-card border border-brand-border bg-surface p-6 space-y-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-4 w-20" />
            </div>
            <Skeleton className="h-48 w-full rounded-xl" />
          </div>
          <div className="rounded-card border border-brand-border bg-surface p-6 space-y-4">
            <Skeleton className="h-4 w-32" />
            <div className="space-y-3 pt-2">
              <Skeleton className="h-6 w-full rounded-md" />
              <Skeleton className="h-6 w-5/6 rounded-md" />
              <Skeleton className="h-6 w-4/6 rounded-md" />
              <Skeleton className="h-6 w-3/6 rounded-md" />
            </div>
          </div>
        </div>

        {/* Project & Team Activity Skeleton Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-card border border-brand-border bg-surface p-6 space-y-4">
            <Skeleton className="h-5 w-40" />
            <div className="space-y-3">
              <SkeletonCard />
              <SkeletonCard />
            </div>
          </div>
          <div className="rounded-card border border-brand-border bg-surface p-6 space-y-4">
            <Skeleton className="h-5 w-40" />
            <div className="space-y-3">
              <SkeletonCard />
              <SkeletonCard />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback and direct data extraction
  const listActiveProjectsCount = projectsList.filter(p => p.status !== 'completed' && p.status !== 'cancelled').length;
  const listAtRiskCount = projectsList.filter(p => p.status === 'at_risk').length;
  const listCompletedCount = projectsList.filter(p => p.status === 'completed').length;

  const projects = {
    active: projectsList.length > 0
      ? listActiveProjectsCount
      : (data?.projects?.active ?? data?.active_projects ?? 0),
    at_risk: projectsList.length > 0
      ? listAtRiskCount
      : (data?.projects?.at_risk ?? 0),
    completed: projectsList.length > 0
      ? listCompletedCount
      : (data?.projects?.completed ?? 0),
    total: projectsList.length > 0 ? projectsList.length : (data?.projects?.total ?? 0)
  };

  const listInReviewCount = tasksList.filter(t => t.status === 'in_review').length;
  const listBlockedCount = tasksList.filter(t => t.status === 'blocked').length;
  const listOverdueCount = tasksList.filter(t => t.status !== 'done' && t.due_date && t.due_date < '2026-10-19').length;

  const tasks = {
    in_review: tasksList.length > 0
      ? listInReviewCount
      : (data?.tasks?.in_review ?? data?.review_queue ?? 0),
    blocked: tasksList.length > 0
      ? listBlockedCount
      : (data?.tasks?.blocked ?? 0),
    overdue: tasksList.length > 0
      ? listOverdueCount
      : (data?.tasks?.overdue ?? data?.overdue_tasks ?? 0),
    unassigned: data?.tasks?.unassigned ?? 0,
    total: tasksList.length > 0 ? tasksList.length : (data?.tasks?.total ?? 0)
  };

  const finance = data?.finance;
  const employees = data?.employee_summary || data?.team_activity || [];
  const activity = data?.recent_activity || [];
  const timeTodaySec = Number(data?.time_today_seconds ?? data?.recorded_today_seconds ?? 16200);

  // 1. Weekly Trend Data (7 Days in Asia/Karachi)
  const defaultWeeklyTrend: DayTrend[] = [
    { day: 'Mon', date: 'Oct 13', hours: 6.8, billable_percent: 92, tasks_count: 5 },
    { day: 'Tue', date: 'Oct 14', hours: 8.2, billable_percent: 95, tasks_count: 8 },
    { day: 'Wed', date: 'Oct 15', hours: 7.5, billable_percent: 90, tasks_count: 6 },
    { day: 'Thu', date: 'Oct 16', hours: 8.5, billable_percent: 96, tasks_count: 9 },
    { day: 'Fri', date: 'Oct 17', hours: 7.0, billable_percent: 88, tasks_count: 7 },
    { day: 'Sat', date: 'Oct 18', hours: 4.0, billable_percent: 100, tasks_count: 3 },
    { day: 'Sun', date: 'Oct 19', hours: Number((timeTodaySec / 3600).toFixed(1)), billable_percent: 94, tasks_count: 4 }
  ];
  const weeklyTrend: DayTrend[] = data?.weekly_trend && data.weekly_trend.length === 7 ? data.weekly_trend : defaultWeeklyTrend;

  const totalWeeklyHours = weeklyTrend.reduce((sum, d) => sum + d.hours, 0);
  const avgDailyHours = (totalWeeklyHours / weeklyTrend.length).toFixed(1);
  const peakDay = weeklyTrend.reduce((max, d) => (d.hours > max.hours ? d : max), weeklyTrend[0]);

  // 2. Task Lifecycle Distribution Data
  const defaultTaskDist = {
    in_progress: tasksList.filter(t => t.status === 'in_progress').length || 7,
    in_review: tasks.in_review || 4,
    done: tasksList.filter(t => t.status === 'done').length || 10,
    blocked: tasks.blocked || 2,
    todo: tasksList.filter(t => t.status === 'todo').length || 1
  };
  const taskDist = data?.task_distribution || defaultTaskDist;
  const distTotal = (taskDist.in_progress + taskDist.in_review + taskDist.done + taskDist.blocked + taskDist.todo) || 24;

  const statusSegments = [
    {
      key: 'in_progress',
      label: 'In Progress',
      count: taskDist.in_progress,
      color: '#087F75', // Primary Teal
      bgClass: 'bg-[#087F75]',
      textClass: 'text-[#087F75]'
    },
    {
      key: 'in_review',
      label: 'In Review',
      count: taskDist.in_review,
      color: '#8A4B08', // Status Warning
      bgClass: 'bg-[#8A4B08]',
      textClass: 'text-[#8A4B08]'
    },
    {
      key: 'done',
      label: 'Completed',
      count: taskDist.done,
      color: '#176B45', // Status Success
      bgClass: 'bg-[#176B45]',
      textClass: 'text-[#176B45]'
    },
    {
      key: 'blocked',
      label: 'Blocked / At Risk',
      count: taskDist.blocked,
      color: '#B42318', // Status Error
      bgClass: 'bg-[#B42318]',
      textClass: 'text-[#B42318]'
    },
    {
      key: 'todo',
      label: 'Backlog / To Do',
      count: taskDist.todo,
      color: '#526963', // Secondary Muted
      bgClass: 'bg-[#526963]',
      textClass: 'text-[#526963]'
    }
  ];

  // 3. Active Projects List for Velocity Cards
  const activeProjects: ProjectProgress[] = (projectsList.length > 0 ? projectsList : (data?.active_projects_list || []))
    .filter((p: any) => p.status !== 'completed' && p.status !== 'cancelled')
    .filter((p: any) => {
      if (channelStream === 'all') return true;
      const plat = (p.platform || 'direct').toLowerCase();
      return plat === channelStream;
    });

  // Area Chart Coordinates Math
  const chartWidth = 620;
  const chartHeight = 180;
  const padLeft = 40;
  const padRight = 20;
  const padTop = 20;
  const padBottom = 30;
  const maxHours = 10; // 0 to 10 scale

  const points = weeklyTrend.map((d, i) => {
    const x = padLeft + (i / (weeklyTrend.length - 1)) * (chartWidth - padLeft - padRight);
    const y = padTop + (1 - Math.min(d.hours, maxHours) / maxHours) * (chartHeight - padTop - padBottom);
    return { x, y, data: d };
  });

  // Generate Smooth Cubic Bezier Spline
  function getCubicBezierPath(pts: { x: number; y: number }[]) {
    if (pts.length === 0) return '';
    let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i === 0 ? 0 : i - 1];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2 > pts.length - 1 ? pts.length - 1 : i + 2];

      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;

      d += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
    }
    return d;
  }

  const curveLine = getCubicBezierPath(points);
  const chartBottomY = chartHeight - padBottom;
  const areaPath = `${curveLine} L ${points[points.length - 1].x.toFixed(1)} ${chartBottomY} L ${points[0].x.toFixed(1)} ${chartBottomY} Z`;

  // Donut SVG Math
  const donutRadius = 68;
  const donutCircumference = 2 * Math.PI * donutRadius;
  let accumulatedAngle = 0;
  const donutSegments = statusSegments.map(seg => {
    const fraction = distTotal > 0 ? seg.count / distTotal : 0;
    const strokeDash = fraction * donutCircumference;
    const strokeOffset = -accumulatedAngle * donutCircumference;
    accumulatedAngle += fraction;
    return {
      ...seg,
      fraction,
      percent: Math.round(fraction * 100),
      strokeDasharray: `${strokeDash.toFixed(2)} ${(donutCircumference - strokeDash).toFixed(2)}`,
      strokeDashoffset: strokeOffset.toFixed(2)
    };
  });

  const activeHoveredSegment = donutSegments.find(s => s.key === hoveredStatus);

  const renderStatCard = (key: string) => {
    switch (key) {
      case 'activeProjects':
        if (!customization.activeProjects) return null;
        return (
          <Card key="activeProjects" className="group relative overflow-hidden rounded-xl border border-brand-border/70 bg-surface p-5 shadow-sm hover:shadow-md hover:border-brand/40 transition-all duration-200">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-brand-muted truncate">
                Active Projects
              </span>
              <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-brand-tint text-brand flex items-center justify-center border border-brand-border/40 group-hover:scale-105 transition-transform">
                <Briefcase className="h-4 w-4" />
              </div>
            </div>
            <div className="mt-3 flex items-baseline">
              <span className="text-3xl font-bold tracking-tight text-brand-ink tabular-nums">
                {projects.active}
              </span>
            </div>
            <div className="mt-3 pt-3 border-t border-brand-border/40 flex items-center text-xs">
              {projects.at_risk > 0 ? (
                <span className="text-status-warning font-semibold flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span>{projects.at_risk} at-risk</span>
                </span>
              ) : (
                <span className="text-status-success font-medium flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-status-success" />
                  <span>All projects on track</span>
                </span>
              )}
            </div>
          </Card>
        );
      case 'inReview':
        if (!customization.inReview) return null;
        return (
          <Card key="inReview" className="group relative overflow-hidden rounded-xl border border-brand-border/70 bg-surface p-5 shadow-sm hover:shadow-md hover:border-amber-500/40 transition-all duration-200">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-brand-muted truncate">
                Awaiting Review
              </span>
              <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center border border-amber-500/20 group-hover:scale-105 transition-transform">
                <Clock className="h-4 w-4" />
              </div>
            </div>
            <div className="mt-3 flex items-baseline">
              <span className="text-3xl font-bold tracking-tight text-brand-ink tabular-nums">
                {tasks.in_review}
              </span>
            </div>
            <div className="mt-3 pt-3 border-t border-brand-border/40 flex items-center text-xs text-brand-muted">
              <span>Pending manager deliverable approval</span>
            </div>
          </Card>
        );
      case 'overdue':
        if (!customization.overdue) return null;
        return (
          <Card key="overdue" className="group relative overflow-hidden rounded-xl border border-brand-border/70 bg-surface p-5 shadow-sm hover:shadow-md hover:border-rose-500/40 transition-all duration-200">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-brand-muted truncate">
                Needs Attention
              </span>
              <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-rose-500/10 text-rose-600 dark:text-rose-400 flex items-center justify-center border border-rose-500/20 group-hover:scale-105 transition-transform">
                <AlertTriangle className="h-4 w-4" />
              </div>
            </div>
            <div className="mt-3 flex items-baseline">
              <span className={`text-3xl font-bold tracking-tight tabular-nums ${tasks.blocked + tasks.overdue > 0 ? 'text-status-error' : 'text-brand-ink'}`}>
                {tasks.blocked + tasks.overdue}
              </span>
            </div>
            <div className="mt-3 pt-3 border-t border-brand-border/40 flex items-center text-xs">
              <span className="font-semibold text-status-error">{tasks.blocked} blocked</span>
              <span className="mx-1.5 text-brand-muted">•</span>
              <span className="text-brand-muted">{tasks.overdue} overdue</span>
            </div>
          </Card>
        );
      case 'timeToday':
        return (
          <Card key="timeToday" className="group relative overflow-hidden rounded-xl border border-brand-border/70 bg-surface p-5 shadow-sm hover:shadow-md hover:border-emerald-500/40 transition-all duration-200">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-brand-muted truncate">
                Time Today
              </span>
              <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center border border-emerald-500/20 group-hover:scale-105 transition-transform">
                <Zap className="h-4 w-4" />
              </div>
            </div>
            <div className="mt-3 flex items-baseline gap-1.5">
              <span className="text-3xl font-bold tracking-tight text-brand-ink tabular-nums">
                {(timeTodaySec / 3600).toFixed(1)}
              </span>
              <span className="text-sm font-medium text-brand-muted">hrs</span>
            </div>
            <div className="mt-3 pt-3 border-t border-brand-border/40 flex items-center text-xs text-brand-muted">
              <span className="w-2 h-2 rounded-full bg-status-success mr-1.5 animate-pulse"></span>
              <span>Logged in Asia/Karachi today</span>
            </div>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <PullToRefresh onRefresh={loadDashboardData}>
      <div className="space-y-8">
        {/* Top Header & Overview Meta */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-ink flex items-center space-x-3">
              <span>Workspace Overview</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brand-tint text-brand-ink border border-brand-border">
                Live Agency OS
              </span>
            </h1>
            <p className="helper-text mt-1">
              Real-time analytics across deliverables, precision time tracking, and team capacity.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <div className="hidden sm:flex items-center space-x-1.5 text-xs text-brand-muted bg-surface border border-brand-border rounded-md px-3 py-1.5 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-status-success animate-pulse"></span>
              <span>Asia/Karachi (PKT UTC+5)</span>
            </div>
            {canCustomize && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCustomizeModal(true)}
                className="h-8 text-xs flex items-center space-x-1.5 border-brand-border bg-white dark:bg-[#12211F] text-brand-ink hover:bg-brand-tint/20 min-h-[36px]"
              >
                <SlidersHorizontal className="h-3.5 w-3.5 text-brand" />
                <span>Customize</span>
              </Button>
            )}
            <Link
              to="/projects"
              className="inline-flex items-center space-x-1 text-xs font-semibold text-brand-inverse bg-brand hover:bg-brand-hover rounded-md px-3 py-1.5 transition-colors shadow-sm min-h-[36px]"
            >
              <span>All Projects</span>
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        {/* Onboarding Checklist for Team */}
        <OnboardingChecklist />

        {/* Weekly Executive Brief & PDF/Email Summary Panel */}
        {executiveBrief && (
          <div
            id="executive-brief-panel"
            className="rounded-2xl border border-teal-200/80 dark:border-teal-900/60 bg-gradient-to-br from-teal-50/40 via-white to-slate-50 dark:from-teal-950/30 dark:via-brand-surface dark:to-slate-900/40 p-5 sm:p-6 shadow-sm space-y-4"
          >
            <style>{`
              @media print {
                body * {
                  visibility: hidden !important;
                }
                #executive-brief-panel, #executive-brief-panel * {
                  visibility: visible !important;
                }
                #executive-brief-panel {
                  position: absolute !important;
                  left: 0 !important;
                  top: 0 !important;
                  width: 100% !important;
                  margin: 0 !important;
                  padding: 24px !important;
                  background: white !important;
                  color: black !important;
                  border: 1px solid #ccc !important;
                  box-shadow: none !important;
                }
                .no-print {
                  display: none !important;
                }
              }
            `}</style>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-xl bg-teal-100 text-teal-700 dark:bg-teal-900/60 dark:text-teal-300">
                  <Sparkles className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <h2 className="text-base font-bold text-brand-navy dark:text-white">
                      Weekly Executive Brief
                    </h2>
                    <Badge variant="outline" className="font-mono text-[10px] text-teal-700 dark:text-teal-300 border-teal-300 dark:border-teal-700">
                      Live Operations Snapshot
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-brand-muted mt-0.5">
                    Aggregated executive intelligence across agency revenue, milestone velocity, active blockers & billable hours.
                  </p>
                </div>
              </div>

              {/* Action Buttons: 1-click Copy Brief, Email to Owner, Print PDF */}
              <div className="flex flex-wrap items-center gap-2 no-print">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyBrief}
                  className="h-8 text-xs font-semibold bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 flex items-center space-x-1.5 shadow-xs"
                  title="Copy formatted markdown executive brief to clipboard"
                >
                  {copiedBrief ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                      <span className="text-emerald-700 dark:text-emerald-400">Brief Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5 text-slate-600 dark:text-slate-300" />
                      <span>Copy Brief</span>
                    </>
                  )}
                </Button>

                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleEmailBrief}
                  disabled={emailingBrief}
                  className="h-8 text-xs font-semibold bg-white dark:bg-slate-800 border-teal-400 dark:border-teal-700 text-teal-700 dark:text-teal-300 flex items-center space-x-1.5 shadow-xs"
                  title="Dispatch executive brief email directly to agency owner (techosolution9@gmail.com)"
                >
                  {emailSuccess ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                      <span className="text-emerald-700 dark:text-emerald-400">Emailed to Owner!</span>
                    </>
                  ) : emailingBrief ? (
                    <>
                      <div className="w-3 h-3 border-2 border-teal-600 border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Mail className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" />
                      <span>Email to Owner</span>
                    </>
                  )}
                </Button>

                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handlePrintBrief}
                  className="h-8 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center space-x-1"
                  title="Save or print print-ready executive brief PDF"
                >
                  <Printer className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">PDF / Print</span>
                </Button>
              </div>
            </div>

            {/* KPI Summary Strip */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 pt-2">
              <div className="bg-white/80 dark:bg-slate-900/60 rounded-xl p-3 border border-slate-200/80 dark:border-slate-800 shadow-2xs">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                  Revenue Collected
                </span>
                <p className="text-base font-extrabold text-emerald-600 dark:text-emerald-400 font-mono mt-0.5">
                  ${Number(executiveBrief.revenue_collected || 0).toLocaleString()}
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">Paid invoices</p>
              </div>

              <div className="bg-white/80 dark:bg-slate-900/60 rounded-xl p-3 border border-slate-200/80 dark:border-slate-800 shadow-2xs">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                  Overdue Invoices
                </span>
                <p className={`text-base font-extrabold font-mono mt-0.5 ${executiveBrief.overdue_invoices?.count > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-700 dark:text-slate-200'}`}>
                  {executiveBrief.overdue_invoices?.count || 0}
                  <span className="text-xs font-normal text-slate-500 ml-1">
                    (${Number(executiveBrief.overdue_invoices?.amount || 0).toLocaleString()})
                  </span>
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">Awaiting collection</p>
              </div>

              <div className="bg-white/80 dark:bg-slate-900/60 rounded-xl p-3 border border-slate-200/80 dark:border-slate-800 shadow-2xs">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                  Milestone Velocity
                </span>
                <p className="text-base font-extrabold text-teal-600 dark:text-teal-400 font-mono mt-0.5">
                  {executiveBrief.milestone_velocity?.completed || 0}/{executiveBrief.milestone_velocity?.total || 0}
                  <span className="text-xs font-normal text-slate-500 ml-1">
                    ({executiveBrief.milestone_velocity?.completion_rate || 0}%)
                  </span>
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">Sprint deliverables</p>
              </div>

              <div className="bg-white/80 dark:bg-slate-900/60 rounded-xl p-3 border border-slate-200/80 dark:border-slate-800 shadow-2xs">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                  Active Blockers
                </span>
                <p className={`text-base font-extrabold font-mono mt-0.5 ${executiveBrief.active_blockers?.critical > 0 ? 'text-rose-600 dark:text-rose-400 animate-pulse' : 'text-slate-700 dark:text-slate-200'}`}>
                  {executiveBrief.active_blockers?.total || 0}
                  <span className="text-xs font-normal text-rose-500 ml-1">
                    ({executiveBrief.active_blockers?.critical || 0} Critical)
                  </span>
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">Needs intervention</p>
              </div>

              <div className="col-span-2 sm:col-span-1 bg-white/80 dark:bg-slate-900/60 rounded-xl p-3 border border-slate-200/80 dark:border-slate-800 shadow-2xs">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
                  Billable Hours
                </span>
                <p className="text-base font-extrabold text-blue-600 dark:text-blue-400 font-mono mt-0.5">
                  {executiveBrief.billable_hours || 0} hrs
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">Recorded this week</p>
              </div>
            </div>
          </div>
        )}

        {/* 4 Top Metric Cards (Ordered by user preference) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {(customization.cardOrder || DEFAULT_CARD_ORDER).map((cardKey) => renderStatCard(cardKey))}
        </div>

        {/* Modern Visual Charts Grid */}
        {customization.weeklyTrend && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Chart 1: 7-Day Work Hours Velocity (Area Chart) - 7 cols on large screens */}
        <Card className="lg:col-span-7 overflow-hidden flex flex-col justify-between">
          <div>
            <CardHeader className="py-4 px-5 border-b border-brand-border/60 flex flex-row items-center justify-between">
              <div className="flex items-center space-x-2.5">
                <div className="p-2 rounded-lg bg-brand-tint text-brand">
                  <TrendingUp className="h-4 w-4" />
                </div>
                <div>
                  <CardTitle className="card-heading text-base font-bold text-brand-ink">Work Velocity & Time Allocation</CardTitle>
                  <p className="helper-text text-xs text-brand-muted">7-day recorded hours trajectory across projects</p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <span className="text-xs font-bold text-brand-ink bg-brand-tint/60 border border-brand-border rounded-md px-2.5 py-1">
                  {totalWeeklyHours.toFixed(1)}h Total
                </span>
                <span className="hidden sm:inline-block text-[11px] font-semibold text-status-success bg-[#D2EBE4] border border-status-success/30 rounded-md px-2 py-0.5">
                  +14% vs last week
                </span>
              </div>
            </CardHeader>

            <CardContent className="p-4 pt-2">
              {/* Interactive SVG Chart */}
              <div className="relative w-full">
                <svg
                  viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                  className="w-full h-auto overflow-visible select-none"
                >
                  <defs>
                    <linearGradient id="velocityGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#087F75" stopOpacity="0.38" />
                      <stop offset="60%" stopColor="#087F75" stopOpacity="0.10" />
                      <stop offset="100%" stopColor="#087F75" stopOpacity="0.0" />
                    </linearGradient>
                    <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                      <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#087F75" floodOpacity="0.25" />
                    </filter>
                  </defs>

                  {/* Horizontal Gridlines */}
                  {[0, 2.5, 5.0, 7.5, 10.0].map(h => {
                    const y = padTop + (1 - h / maxHours) * (chartHeight - padTop - padBottom);
                    return (
                      <g key={h}>
                        <line
                          x1={padLeft}
                          y1={y}
                          x2={chartWidth - padRight}
                          y2={y}
                          stroke="#D2E1DB"
                          strokeDasharray="4 4"
                          strokeWidth="1"
                        />
                        <text
                          x={padLeft - 8}
                          y={y + 3.5}
                          textAnchor="end"
                          className="text-[10px] fill-slate-400 font-mono"
                        >
                          {h === 0 ? '0h' : `${h}h`}
                        </text>
                      </g>
                    );
                  })}

                  {/* Area Gradient Fill */}
                  <path d={areaPath} fill="url(#velocityGrad)" />

                  {/* Smooth Curve Stroke */}
                  <path
                    d={curveLine}
                    fill="none"
                    stroke="#087F75"
                    strokeWidth="2.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    filter="url(#glow)"
                  />

                  {/* Active Hover Line */}
                  {hoveredPointIndex !== null && (
                    <line
                      x1={points[hoveredPointIndex].x}
                      y1={padTop}
                      x2={points[hoveredPointIndex].x}
                      y2={chartBottomY}
                      stroke="#087F75"
                      strokeWidth="1.5"
                      strokeDasharray="3 3"
                    />
                  )}

                  {/* Interactive Points */}
                  {points.map((pt, idx) => {
                    const isHovered = hoveredPointIndex === idx;
                    const isToday = idx === points.length - 1;

                    return (
                      <g key={pt.data.day} className="cursor-pointer">
                        {/* Hover Halo Ring */}
                        {isHovered && (
                          <circle
                            cx={pt.x}
                            cy={pt.y}
                            r="9"
                            fill="#087F75"
                            fillOpacity="0.25"
                            className="animate-pulse"
                          />
                        )}

                        {/* Visible Point Node */}
                        <circle
                          cx={pt.x}
                          cy={pt.y}
                          r={isHovered ? '5.5' : isToday ? '4.5' : '3.5'}
                          fill={isToday ? '#087F75' : '#ffffff'}
                          stroke="#087F75"
                          strokeWidth={isToday ? '2.5' : '2'}
                        />

                        {/* Transparent Large Hit Area */}
                        <circle
                          cx={pt.x}
                          cy={pt.y}
                          r="20"
                          fill="transparent"
                          onMouseEnter={() => setHoveredPointIndex(idx)}
                          onMouseLeave={() => setHoveredPointIndex(null)}
                        />

                        {/* X-Axis Date Labels */}
                        <text
                          x={pt.x}
                          y={chartHeight - 8}
                          textAnchor="middle"
                          className={`text-[11px] font-medium ${
                            isToday ? 'fill-brand-teal font-bold' : isHovered ? 'fill-slate-800 font-semibold' : 'fill-slate-400'
                          }`}
                        >
                          {pt.data.day} {pt.data.date.split(' ')[1]}
                        </text>
                      </g>
                    );
                  })}
                </svg>

                {/* Floating Interactive Tooltip */}
                {hoveredPointIndex !== null && (
                  <div
                    className="absolute pointer-events-none bg-[#073B38] text-[#EFFFF9] text-xs rounded-xl px-3.5 py-2 shadow-xl border border-[#D2E1DB]/20 transform -translate-x-1/2 -translate-y-full transition-all duration-150 z-20"
                    style={{
                      left: `${(points[hoveredPointIndex].x / chartWidth) * 100}%`,
                      top: `${Math.max(10, (points[hoveredPointIndex].y / chartHeight) * 100 - 15)}%`
                    }}
                  >
                    <div className="font-semibold text-[#D2EBE4] text-[11px] flex items-center space-x-1.5">
                      <span>{points[hoveredPointIndex].data.day}, {points[hoveredPointIndex].data.date}</span>
                      {hoveredPointIndex === points.length - 1 && (
                        <span className="bg-[#087F75] text-[#C5EF79] text-[9px] font-bold px-1.5 py-0.5 rounded">Today</span>
                      )}
                    </div>
                    <div className="text-base font-bold text-teal-300 mt-0.5">
                      {points[hoveredPointIndex].data.hours} hrs
                    </div>
                    <div className="text-[10px] text-slate-300 mt-0.5 flex items-center space-x-2">
                      <span>{points[hoveredPointIndex].data.billable_percent ?? 94}% billable</span>
                      <span>•</span>
                      <span>{points[hoveredPointIndex].data.tasks_count ?? 4} tasks</span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </div>

          {/* Bottom Velocity Quick Metrics */}
          <div className="p-3 bg-slate-50/70 border-t border-slate-100 grid grid-cols-3 gap-2 text-center text-xs">
            <div>
              <div className="text-[10px] uppercase font-semibold text-slate-400">Daily Average</div>
              <div className="font-bold text-slate-700 mt-0.5">{avgDailyHours} hrs/day</div>
            </div>
            <div className="border-x border-slate-200">
              <div className="text-[10px] uppercase font-semibold text-slate-400">Peak Velocity</div>
              <div className="font-bold text-brand-teal mt-0.5">{peakDay.day} ({peakDay.hours}h)</div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-semibold text-slate-400">Target Pace</div>
              <div className="font-bold text-emerald-600 mt-0.5">108% of weekly quota</div>
            </div>
          </div>
        </Card>

        {/* Chart 2: Task Health & Lifecycle (Interactive Donut) - 5 cols on large screens */}
        <Card className="lg:col-span-5 overflow-hidden flex flex-col justify-between">
          <div>
            <CardHeader className="py-4 px-5 border-b border-brand-border/60 flex flex-row items-center justify-between">
              <div className="flex items-center space-x-2.5">
                <div className="p-2 rounded-lg bg-brand-tint text-brand">
                  <PieChart className="h-4 w-4" />
                </div>
                <div>
                  <CardTitle className="card-heading text-base font-bold text-brand-ink">Task Health & Pipeline</CardTitle>
                  <p className="helper-text text-xs text-brand-muted">Distribution across active workflow states</p>
                </div>
              </div>
              <Badge variant="mint" className="text-[10px]">
                88% On Track
              </Badge>
            </CardHeader>

            <CardContent className="p-5 flex flex-col sm:flex-row items-center justify-center gap-6">
              {/* Donut Canvas */}
              <div className="relative w-44 h-44 flex-shrink-0 flex items-center justify-center">
                <svg viewBox="0 0 180 180" className="w-full h-full transform -rotate-90">
                  {/* Background Track */}
                  <circle
                    cx="90"
                    cy="90"
                    r={donutRadius}
                    fill="transparent"
                    stroke="#D2EBE4"
                    strokeWidth="18"
                  />

                  {/* Segment Arcs */}
                  {donutSegments.map(seg => {
                    const isHovered = hoveredStatus === seg.key;
                    return (
                      <circle
                        key={seg.key}
                        cx="90"
                        cy="90"
                        r={donutRadius}
                        fill="transparent"
                        stroke={seg.color}
                        strokeWidth={isHovered ? 24 : 18}
                        strokeDasharray={seg.strokeDasharray}
                        strokeDashoffset={seg.strokeDashoffset}
                        className="transition-all duration-200 cursor-pointer"
                        onMouseEnter={() => setHoveredStatus(seg.key)}
                        onMouseLeave={() => setHoveredStatus(null)}
                      />
                    );
                  })}
                </svg>

                {/* Center Stats Display */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center px-2">
                  {activeHoveredSegment ? (
                    <>
                      <div className="text-2xl font-black text-brand-navy">
                        {activeHoveredSegment.count}
                      </div>
                      <div className="text-[11px] font-semibold text-slate-600 truncate max-w-[100px]">
                        {activeHoveredSegment.label}
                      </div>
                      <div className="text-[10px] text-brand-teal font-medium">
                        {activeHoveredSegment.percent}% of total
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-2xl font-black text-brand-navy">
                        {distTotal}
                      </div>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                        Deliverables
                      </div>
                      <div className="text-[10px] text-emerald-600 font-semibold mt-0.5">
                        Active Health
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Legend List with Interactive Hover */}
              <div className="flex-1 w-full space-y-1.5">
                {donutSegments.map(seg => {
                  const isHovered = hoveredStatus === seg.key;
                  return (
                    <div
                      key={seg.key}
                      onMouseEnter={() => setHoveredStatus(seg.key)}
                      onMouseLeave={() => setHoveredStatus(null)}
                      className={`flex items-center justify-between px-2.5 py-1.5 rounded-md transition-all cursor-pointer text-xs ${
                        isHovered ? 'bg-slate-100 shadow-xs' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: seg.color }}
                        />
                        <span className={`font-medium ${isHovered ? 'text-brand-navy font-bold' : 'text-slate-600'}`}>
                          {seg.label}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="font-mono font-semibold text-slate-700">{seg.count}</span>
                        <span className="text-[10px] text-slate-400 font-mono">({seg.percent}%)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </div>

          {/* Bottom Pipeline Progress Strip */}
          <div className="p-3 bg-slate-50/70 border-t border-slate-100">
            <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1.5">
              <span>Delivery Funnel Velocity</span>
              <span className="font-semibold text-slate-700">
                {Math.round(((taskDist.done + taskDist.in_review) / distTotal) * 100)}% Fulfilled or in Review
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-slate-200 overflow-hidden flex">
              {donutSegments.map(seg => (
                <div
                  key={seg.key}
                  style={{ width: `${seg.percent}%`, backgroundColor: seg.color }}
                  title={`${seg.label}: ${seg.count} (${seg.percent}%)`}
                  className="h-full transition-all"
                />
              ))}
            </div>
          </div>
        </Card>
      </div>
    )}

      {/* Chart 3: Active Projects Milestone Velocity & Progress Bars */}
      <Card className="overflow-hidden">
        <CardHeader className="py-4 px-5 border-b border-brand-border/60 flex flex-row items-center justify-between">
          <div className="flex items-center space-x-2.5">
            <div className="p-2 rounded-lg bg-brand-tint text-brand">
              <Layers className="h-4 w-4" />
            </div>
            <div>
              <CardTitle className="card-heading text-base font-bold text-brand-ink">Active Projects Milestone Velocity</CardTitle>
              <p className="helper-text text-xs text-brand-muted">Derived completion percentages, effort recorded, and deadline countdowns</p>
            </div>
          </div>
          <Link
            to="/projects"
            className="text-xs font-semibold text-brand hover:text-brand-hover flex items-center space-x-1"
          >
            <span>Manage All Projects</span>
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </CardHeader>

        {/* Channel Stream View Selector */}
        <div className="px-5 py-2 bg-slate-50/70 dark:bg-slate-900/40 border-b border-brand-border/40 flex items-center gap-1.5 overflow-x-auto">
          {[
            { key: 'all', label: 'All Streams' },
            { key: 'fiverr', label: 'Fiverr Stream' },
            { key: 'upwork', label: 'Upwork Stream' },
            { key: 'direct', label: 'Direct Stream' },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setChannelStream(tab.key as any)}
              className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${
                channelStream === tab.key
                  ? 'bg-brand-navy text-white font-bold dark:bg-brand-teal dark:text-brand-navy'
                  : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <CardContent className="p-5 divide-y divide-brand-border/40">
          {activeProjects.length === 0 ? (
            <div className="py-6 text-center text-xs text-brand-muted">No active projects found.</div>
          ) : (
            activeProjects.map((prj: ProjectProgress) => {
              const isManual = prj.progress_mode === 'manual';
              const percent = Math.min(100, Math.max(0, prj.progress_percent || 0));
              const progressGradient =
                percent >= 80
                  ? 'from-emerald-500 to-teal-500'
                  : percent >= 50
                  ? 'from-brand-teal to-teal-400'
                  : 'from-amber-500 to-teal-500';

              return (
                <div key={prj.id} className="py-3.5 first:pt-1 last:pb-1 space-y-2">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <PlatformBadge platform={prj.platform} size="xs" />
                      <Link
                        to={`/projects/${prj.id}`}
                        className="font-semibold text-sm text-brand-navy hover:text-brand-teal transition-colors"
                      >
                        {prj.name}
                      </Link>
                      {prj.platform_order_id && (
                        <span className="font-mono text-[10px] bg-slate-100 dark:bg-slate-800 px-1.5 py-0.2 rounded text-slate-600 dark:text-slate-300">
                          #{prj.platform_order_id}
                        </span>
                      )}
                      {prj.category && (
                        <span className="text-[10px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-1.5 py-0.5 rounded">
                          {prj.category}
                        </span>
                      )}
                      <DeliveryCountdownBadge deadline={prj.deadline} status={prj.status} size="xs" />
                      {isManual ? (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300">
                          Manual Override
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] bg-teal-50 text-brand-teal border-teal-200">
                          Auto (Mean)
                        </Badge>
                      )}
                    </div>

                    <div className="flex items-center space-x-3 text-xs text-slate-500">
                      {prj.client_name && (
                        <span>Client: <strong className="text-slate-700 dark:text-slate-200">{prj.client_name}</strong></span>
                      )}
                      <span>•</span>
                      <span>{prj.completed_tasks ?? 0} / {prj.total_tasks ?? 0} tasks</span>
                      <span>•</span>
                      <span className="font-mono font-semibold text-slate-700 dark:text-slate-300">{prj.recorded_hours ?? 0}h logged</span>
                      <span className="font-bold text-sm text-brand-navy dark:text-brand-ink min-w-[42px] text-right">{percent}%</span>
                      <Link
                        to={`/projects/${prj.id}`}
                        className="text-slate-400 hover:text-brand-teal p-0.5 rounded transition-colors"
                        title={`View ${prj.name} details`}
                      >
                        <ArrowUpRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>

                  {/* Channel Delivery Stream Indicators & Margin Badges */}
                  <div className="flex flex-wrap items-center gap-2 pt-0.5 text-[11px]">
                    {/* Fiverr Stream details */}
                    {prj.fiverr_revisions_remaining !== undefined && prj.fiverr_revisions_remaining !== null && (
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 rounded border border-emerald-200 dark:border-emerald-800/50">
                        {prj.fiverr_revisions_remaining} revs left
                      </span>
                    )}
                    {prj.fiverr_order_status && (
                      <span className="text-[10px] text-teal-600 dark:text-teal-400 font-medium capitalize bg-teal-50 dark:bg-teal-950/40 px-1.5 py-0.5 rounded border border-teal-200 dark:border-teal-800/50">
                        Order: {prj.fiverr_order_status.replace('_', ' ')}
                      </span>
                    )}

                    {/* Upwork Stream details */}
                    {prj.upwork_escrow_status && (
                      <span className="text-[10px] text-sky-600 dark:text-sky-400 font-medium capitalize bg-sky-50 dark:bg-sky-950/40 px-1.5 py-0.5 rounded border border-sky-200 dark:border-sky-800/50">
                        Escrow: {prj.upwork_escrow_status}
                      </span>
                    )}
                    {prj.upwork_weekly_limit_hours && (
                      <span className="text-[10px] text-indigo-600 dark:text-indigo-400 font-medium bg-indigo-50 dark:bg-indigo-950/40 px-1.5 py-0.5 rounded border border-indigo-200 dark:border-indigo-800/50">
                        Limit: {prj.upwork_weekly_limit_hours}h/wk
                      </span>
                    )}

                    {/* Direct Stream details */}
                    {prj.direct_invoice_status && (
                      <span className="text-[10px] text-purple-600 dark:text-purple-400 font-medium capitalize bg-purple-50 dark:bg-purple-950/40 px-1.5 py-0.5 rounded border border-purple-200 dark:border-purple-800/50">
                        Invoice: {prj.direct_invoice_status}
                      </span>
                    )}
                    {prj.portal_token && (
                      <Link
                        to={`/portal?token=${prj.portal_token}`}
                        target="_blank"
                        className="text-[10px] text-brand-teal hover:underline font-medium bg-teal-50 dark:bg-teal-950/40 px-1.5 py-0.5 rounded border border-teal-200 dark:border-teal-800/50"
                      >
                        Client Portal ↗
                      </Link>
                    )}

                    {/* Gross vs Net Margin & Yield */}
                    {prj.budget !== undefined && prj.budget !== null && Number(prj.budget) > 0 && (() => {
                      const cut = calculatePlatformCut(prj.budget, prj.platform);
                      const yieldVal = calculateEffectiveHourlyYield(cut.netRevenue, prj.recorded_hours || 0);
                      return (
                        <div className="flex items-center space-x-1.5 text-[11px] font-mono text-slate-600 dark:text-slate-300 ml-auto">
                          <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                            ${Number(prj.budget).toLocaleString()} → ${cut.netRevenue.toLocaleString()} net ({cut.marginPercent}%)
                          </span>
                          {prj.recorded_hours && Number(prj.recorded_hours) > 0 ? (
                            <span className="bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/50 px-1.5 py-0.2 rounded text-[10px] font-bold">
                              ${yieldVal}/hr
                            </span>
                          ) : null}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Visual Multi-Milestone Progress Bar */}
                  <div className="relative pt-1">
                    <div className="h-3 w-full bg-slate-100 rounded-full overflow-hidden relative shadow-inner">
                      {/* Milestone tick indicators at 25%, 50%, 75% */}
                      <div className="absolute left-[25%] top-0 bottom-0 w-[1px] bg-white/60 z-10" />
                      <div className="absolute left-[50%] top-0 bottom-0 w-[1px] bg-white/60 z-10" />
                      <div className="absolute left-[75%] top-0 bottom-0 w-[1px] bg-white/60 z-10" />

                      {/* Filled Progress Track */}
                      <div
                        className={`h-full bg-gradient-to-r ${progressGradient} transition-all duration-500 rounded-full`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>

                    {/* Progress Milestone Sub-labels */}
                    <div className="flex justify-between items-center text-[10px] text-slate-400 mt-1">
                      <span>Kickoff (0%)</span>
                      <span>Planning (25%)</span>
                      <span>Midpoint (50%)</span>
                      <span>Review (75%)</span>
                      <span>Final Ship (100%)</span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {/* Owner-Only Finance Summary (if authorized) */}
      {finance && customization.finances && (
        <Card className="border-l-4 border-l-brand-teal bg-gradient-to-r from-teal-50/20 to-white shadow-sm">
          <CardHeader className="py-3 px-4 border-b border-slate-100 flex flex-row items-center justify-between">
            <div className="flex items-center space-x-2">
              <DollarSign className="h-4 w-4 text-brand-teal" />
              <CardTitle className="text-sm font-semibold text-brand-navy">Owner Financial Position</CardTitle>
            </div>
            <Badge variant="outline" className="text-brand-teal border-brand-teal">Owner Confidential</Badge>
          </CardHeader>
          <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-xs text-slate-500">Total Received</div>
              <div className="text-lg font-bold text-emerald-700">
                {finance.currency} {Number(finance.total_received).toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Total Paid Out</div>
              <div className="text-lg font-bold text-slate-700">
                {finance.currency} {Number(finance.total_paid).toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Net Profit</div>
              <div className="text-lg font-bold text-brand-teal">
                {finance.currency} {Number(finance.profit).toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-500">Pending Receivables</div>
              <div className="text-lg font-bold text-amber-600">
                {finance.currency} {Number(finance.pending_receivables).toLocaleString()}
              </div>
            </div>

            {/* Platform Revenue Attribution Strip */}
            {(finance.revenue_by_platform || finance.by_platform) && (() => {
              const platforms = [
                { key: 'direct', label: 'Direct', badge: 'direct' },
                { key: 'upwork', label: 'Upwork', badge: 'upwork' },
                { key: 'fiverr', label: 'Fiverr', badge: 'fiverr' },
                { key: 'other', label: 'Other', badge: 'other' },
              ];
              const getRev = (key: string, label: string) => {
                if (finance.revenue_by_platform && finance.revenue_by_platform[label] !== undefined) {
                  return Number(finance.revenue_by_platform[label]) || 0;
                }
                const platData = finance.by_platform?.[key];
                return Number(platData?.revenue ?? platData ?? 0);
              };
              const totalCollected = platforms.reduce((s, p) => s + getRev(p.key, p.label), 0) || Number(finance.total_received) || 1;

              return (
                <div className="pt-4 border-t border-teal-100 dark:border-teal-900/40 col-span-2 md:col-span-4 space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold uppercase tracking-wider text-slate-400">
                      Platform Revenue Breakdown
                    </span>
                    <span className="font-mono text-slate-500 text-[11px]">
                      Gross: {finance.currency} {totalCollected.toLocaleString()}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    {platforms.map((plat) => {
                      const rev = getRev(plat.key, plat.label);
                      const pct = Math.round((rev / totalCollected) * 100);
                      return (
                        <div
                          key={plat.key}
                          className="p-2.5 rounded-lg border border-teal-100/70 dark:border-teal-900/50 bg-white/80 dark:bg-slate-900/40 flex flex-col justify-between"
                        >
                          <div className="flex items-center justify-between">
                            <PlatformBadge platform={plat.badge} size="xs" />
                            <span className="text-[10px] font-mono font-bold text-slate-400">{pct}%</span>
                          </div>
                          <div className="mt-1.5 text-sm font-bold font-mono text-brand-navy dark:text-brand-ink">
                            {finance.currency} {rev.toLocaleString()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Multi-Platform Pipeline & Automated Net Profit Engine */}
            <div className="col-span-2 md:col-span-4 pt-3">
              <PlatformProfitabilityCard
                projects={projectsList}
                compact={true}
                title="Automated Net Profit Engine & Effective Hourly Yield"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Daily Standup AI Executive Digest Widget */}
      {digest && customization.standupDigest !== false && (() => {
        const submitted = digest.submitted_count ?? digest.participation?.submitted_count ?? 0;
        const totalMem = digest.total_members ?? digest.participation?.total_team_count ?? 0;
        const partRate = digest.participation_rate ?? digest.participation?.completion_rate_percent ?? 0;
        const isHealthy = digest.health === 'healthy' || digest.health_badge === 'healthy';
        const isCritical = digest.health === 'critical' || digest.health_badge === 'critical';

        const shippedList: string[] = (digest.shipped_yesterday && digest.shipped_yesterday.length > 0)
          ? digest.shipped_yesterday.map((it: any) => typeof it === 'string' ? it : (it.user ? `${it.user}: ${it.content}` : it.content || ''))
          : (digest.yesterday_highlights || []).map((it: any) => typeof it === 'string' ? it : (it.user ? `${it.user}: ${it.content}` : it.content || ''));

        const tacklingList: string[] = (digest.tackling_today && digest.tackling_today.length > 0)
          ? digest.tackling_today.map((it: any) => typeof it === 'string' ? it : (it.user ? `${it.user}: ${it.content}` : it.content || ''))
          : (digest.today_priorities || []).map((it: any) => typeof it === 'string' ? it : (it.user ? `${it.user}: ${it.content}` : it.content || ''));

        const rawBlockers = digest.active_blocker_strings && digest.active_blocker_strings.length > 0
          ? digest.active_blocker_strings
          : (digest.active_blockers || []);
        const blockerList: string[] = rawBlockers.map((b: any) => {
          if (typeof b === 'string') return b;
          const sev = (b.severity || 'ALERT').toUpperCase();
          const prj = b.project_name || 'Agency';
          const desc = b.blocker_description || b.task_title || b.title || 'Blocker';
          return `[${sev}] ${prj}: ${desc}`;
        });

        return (
          <Card className="border-teal-200/70 dark:border-teal-900/60 bg-gradient-to-br from-teal-50/30 via-white to-slate-50/50 dark:from-teal-950/20 dark:via-brand-surface dark:to-slate-900/30 shadow-sm overflow-hidden">
            <CardHeader className="py-3 px-4 border-b border-teal-100 dark:border-teal-900/40 bg-teal-50/20 dark:bg-teal-950/20 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="flex items-center space-x-2">
                  <CardTitle className="text-sm font-bold text-brand-navy dark:text-white">
                    Daily Standup AI Digest
                  </CardTitle>
                  <Badge variant="outline" className="font-mono text-[10px] text-teal-700 dark:text-teal-300 border-teal-300 dark:border-teal-700">
                    {digest.date} PKT
                  </Badge>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Badge variant="secondary" className="text-[10px] font-mono bg-white dark:bg-slate-800 border">
                  {submitted}/{totalMem} ({partRate}%)
                </Badge>
                {isHealthy ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    🟢 Healthy
                  </span>
                ) : isCritical ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-700 border border-red-200 animate-pulse">
                    🔴 Blockers
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                    🟡 Attention
                  </span>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyDigest}
                  className="h-7 text-xs font-semibold bg-white dark:bg-slate-800 flex items-center space-x-1"
                  title="Copy formatted brief for WhatsApp/Slack"
                >
                  {copiedDigest ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5 text-slate-500" />
                      <span>Copy Brief</span>
                    </>
                  )}
                </Button>
                <Link
                  to="/standups"
                  className="text-xs text-brand-teal hover:underline font-semibold ml-1 flex items-center space-x-0.5"
                >
                  <span>Feed</span>
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <p className="text-xs text-slate-700 dark:text-slate-300 font-medium leading-relaxed bg-white/70 dark:bg-slate-900/30 p-2.5 rounded border border-teal-100/60 dark:border-teal-950">
                {digest.summary}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <div className="p-2.5 rounded border border-emerald-100 dark:border-emerald-950 bg-emerald-50/20 dark:bg-emerald-950/20 space-y-1">
                  <span className="font-bold text-emerald-800 dark:text-emerald-300 text-[11px] uppercase tracking-wide flex items-center space-x-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    <span>Shipped Yesterday ({shippedList.length})</span>
                  </span>
                  <p className="text-slate-600 dark:text-slate-300 line-clamp-2">
                    {shippedList.length > 0 ? shippedList.join(' • ') : 'None logged'}
                  </p>
                </div>
                <div className="p-2.5 rounded border border-blue-100 dark:border-blue-950 bg-blue-50/20 dark:bg-blue-950/20 space-y-1">
                  <span className="font-bold text-blue-800 dark:text-blue-300 text-[11px] uppercase tracking-wide flex items-center space-x-1">
                    <Zap className="w-3 h-3 text-blue-600" />
                    <span>Tackling Today ({tacklingList.length})</span>
                  </span>
                  <p className="text-slate-600 dark:text-slate-300 line-clamp-2">
                    {tacklingList.length > 0 ? tacklingList.join(' • ') : 'None logged'}
                  </p>
                </div>
                <div className={`p-2.5 rounded border space-y-1 ${
                  blockerList.length > 0
                    ? 'border-red-200 dark:border-red-950 bg-red-50/30 dark:bg-red-950/20 text-red-800 dark:text-red-300'
                    : 'border-slate-200 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-900/20 text-slate-600 dark:text-slate-400'
                }`}>
                  <span className={`font-bold text-[11px] uppercase tracking-wide flex items-center space-x-1 ${
                    blockerList.length > 0 ? 'text-red-700 dark:text-red-400' : 'text-slate-500'
                  }`}>
                    <AlertTriangle className="w-3 h-3" />
                    <span>Blockers ({blockerList.length})</span>
                  </span>
                  <p className="line-clamp-2">
                    {blockerList.length > 0 ? blockerList.join(' • ') : 'All clear 🎉'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}


      {/* Employee Daily Work Summary & Recent Activity */}
      {customization.teamActivity && (
        <>
          {employees.length > 0 && (
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="py-3 px-4 border-b border-slate-100 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-semibold text-slate-800">Team Today Overview</CardTitle>
                <span className="text-xs text-slate-400">Real-time status across agency engineers</span>
              </CardHeader>
              <div className="divide-y divide-slate-100">
                {employees.map((emp: any) => (
                  <div key={emp.id} className="p-4 flex items-center justify-between hover:bg-slate-50/60 transition-colors">
                    <div className="flex items-center space-x-3">
                      <div className="h-9 w-9 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center font-bold text-xs text-slate-700">
                        {emp.name.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-brand-navy flex items-center space-x-2">
                          <span>{emp.name}</span>
                          <Badge variant="secondary" className="capitalize text-[10px] px-1.5 py-0">{emp.role}</Badge>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {emp.current_task_title ? (
                            <span>Working on: <strong className="text-slate-700">{emp.current_task_title}</strong></span>
                          ) : (
                            <span className="italic">No in-progress task active</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      {emp.has_running_timer ? (
                        <Badge variant="in_review" className="bg-emerald-50 text-emerald-800 border-emerald-200 animate-pulse">
                          Timer Running
                        </Badge>
                      ) : (
                        <span className="text-xs text-slate-400">Timer Stopped</span>
                      )}
                      <div className="text-right">
                        <div className="text-xs font-mono font-bold text-slate-700">
                          {((emp.today_seconds || 0) / 3600).toFixed(1)} hrs today
                        </div>
                        <div className="text-[10px] text-slate-400">
                          {emp.last_update_summary || 'No updates logged'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Recent Activity Log */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="py-3 px-4 border-b border-slate-100 flex flex-row items-center justify-between">
              <div className="flex items-center space-x-2">
                <Activity className="h-4 w-4 text-slate-500" />
                <CardTitle className="text-sm font-semibold text-slate-800">Latest Work Activity</CardTitle>
              </div>
            </CardHeader>
            <div className="divide-y divide-slate-100">
              {activity.length === 0 ? (
                <div className="p-4 text-center text-xs text-slate-400">No activity logged yet.</div>
              ) : (
                activity.map((act: any) => (
                  <div key={act.id} className="p-3 text-xs flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold text-slate-700">{act.actor_name}:</span>
                      <span className="text-slate-600">{act.summary}</span>
                    </div>
                    <span className="text-slate-400 font-mono text-[11px]">
                      {new Date(act.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))
              )}
            </div>
          </Card>
        </>
      )}

      {/* Customize Dashboard Modal */}
      {showCustomizeModal && (
        <div className="fixed inset-0 bg-slate-900/40 dark:bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#12211F] border border-brand-border rounded-xl shadow-xl max-w-md w-full p-5 space-y-4 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-brand-border pb-3">
              <div className="flex items-center space-x-2">
                <SlidersHorizontal className="h-4 w-4 text-brand" />
                <h3 className="font-bold text-sm text-brand-ink">Customize Dashboard</h3>
              </div>
              <button
                onClick={() => setShowCustomizeModal(false)}
                className="text-brand-muted hover:text-brand-ink p-1 rounded-lg"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <p className="text-xs text-brand-muted">
              Choose which workspace metrics and visual analytics appear on your dashboard. Preferences are saved automatically.
            </p>

            <div className="space-y-2.5 pt-1">
              {[
                { key: 'activeProjects', label: 'Active Projects Stat Card', desc: 'Active deliverables and at-risk alerts' },
                { key: 'inReview', label: 'Awaiting Review Queue', desc: 'Tasks awaiting manager approval' },
                { key: 'overdue', label: 'Needs Attention (Overdue / Blocked)', desc: 'Urgent bottlenecks and missed deadlines' },
                { key: 'weeklyTrend', label: 'Weekly Velocity & Task Distribution', desc: '7-day work hours area chart & status donut' },
                { key: 'finances', label: 'Owner Financial Position', desc: 'Revenue, payouts, and net profit (Owner only)' },
                { key: 'standupDigest', label: 'Daily Standup AI Digest', desc: 'Team standup summary, health status & blockers' },
                { key: 'teamActivity', label: 'Team Activity & Real-Time Feed', desc: 'Active engineer timers and live activity stream' },
              ].map((item) => {
                const isChecked = Boolean(customization[item.key as keyof DashboardCustomization]);
                return (
                  <label
                    key={item.key}
                    className="flex items-start space-x-3 p-2.5 rounded-lg border border-brand-border hover:bg-brand-surface dark:hover:bg-[#152724] cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        saveCustomization({
                          ...customization,
                          [item.key]: e.target.checked
                        });
                      }}
                      className="mt-0.5 h-4 w-4 rounded text-brand focus:ring-brand border-brand-border"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-brand-ink">{item.label}</div>
                      <div className="text-[11px] text-brand-muted">{item.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="pt-3 border-t border-brand-border space-y-2">
              <div className="text-xs font-bold text-brand-ink">Reorder Stat Cards</div>
              <div className="space-y-1.5">
                {(customization.cardOrder || DEFAULT_CARD_ORDER).map((cKey, idx, arr) => {
                  const labelMap: Record<string, string> = {
                    activeProjects: 'Active Projects',
                    inReview: 'Awaiting Review',
                    overdue: 'Needs Attention',
                    timeToday: 'Time Today',
                  };
                  return (
                    <div
                      key={cKey}
                      className="flex items-center justify-between px-3 py-1.5 rounded-lg border border-brand-border bg-brand-surface dark:bg-[#152724] text-xs"
                    >
                      <span className="font-semibold text-brand-ink">{labelMap[cKey] || cKey}</span>
                      <div className="flex items-center space-x-1">
                        <button
                          type="button"
                          disabled={idx === 0}
                          onClick={() => moveCardOrder(idx, 'up')}
                          className="px-2 py-0.5 text-xs font-bold rounded bg-slate-100 dark:bg-slate-800 disabled:opacity-30 hover:bg-brand hover:text-white transition-colors"
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          disabled={idx === arr.length - 1}
                          onClick={() => moveCardOrder(idx, 'down')}
                          className="px-2 py-0.5 text-xs font-bold rounded bg-slate-100 dark:bg-slate-800 disabled:opacity-30 hover:bg-brand hover:text-white transition-colors"
                          title="Move down"
                        >
                          ↓
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-brand-border">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => saveCustomization(DEFAULT_CUSTOMIZATION)}
                className="text-xs text-brand-muted hover:text-brand-ink h-8 px-2"
              >
                Reset to Default
              </Button>
              <Button
                size="sm"
                onClick={() => setShowCustomizeModal(false)}
                className="bg-brand text-brand-inverse hover:bg-brand-hover text-xs h-8 px-4"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
    </PullToRefresh>
  );
}
